import { LitElement, html, nothing } from 'lit';
import { contribution_reports, expense_reports } from '../../../backend/reportDefs.mjs';
import '../styles/login-app.css';

function validateDateRange(start, end) {
  return new Date(start) <= new Date(end);
}

const reports = {
  ...expense_reports,
  ...contribution_reports,
};

class ReportClient extends LitElement {
  createRenderRoot() {
    return this;
  }

  constructor() {
    super();
    const today = new Date();
    const lastWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
    this.startDate = lastWeek.toISOString().split('T')[0];
    this.endDate = today.toISOString().split('T')[0];
    this.isLoading = false;
    this.loadingReports = {};
  }

  async selectReport(reportType) {
    if (!this.startDate || !this.endDate) {
      alert('Please select both start and end dates.');
      return;
    }

    if (!validateDateRange(this.startDate, this.endDate)) {
      alert('The start date must be before the end date.');
      return;
    }

    this.loadingReports = { ...this.loadingReports, [reportType]: true };
    this.requestUpdate();

    try {
      console.log('Selected report type:', reportType);
      const response = await this.fetchReport(reportType);

      const contentType = response.headers.get("content-type") || "";
      if (!response.ok || !contentType.includes("application/pdf")) {
        const errorText = await response.text();
        console.error("Unexpected response:", errorText);
        throw new Error("Failed to fetch valid report PDF.");
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch (error) {
      console.error('Error fetching report:', error);
      alert('Failed to generate the report. Please try again.');
    } finally {
      this.loadingReports = { ...this.loadingReports, [reportType]: false };
      this.requestUpdate();
    }
  }

  async fetchReport(reportType) {
    const supported = reports;
    if (!supported[reportType]) {
      alert(`Report type "${reportType}" not supported yet.`);
      return;
    }

    const reportDomain = location.hostname === 'localhost'
      ? 'pn3reports.afscme.org'
      : location.hostname;

    const reportUrl = `https://${reportDomain}/api/process`;
    //const reportUrl ="https://6ln1q8vqu6.execute-api.us-east-1.amazonaws.com/prod/api/process"
    console.log('Report URL:', reportUrl);

    const token = window.authmgr?.current_login?.id_token;
    if (!token) {
      alert("You're not logged in. Please sign in to continue.");
      throw new Error("Missing JWT token");
    }

    const res = await fetch(reportUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        startDate: this.startDate,
        endDate: this.endDate,
        reportType,
      }),
    });

    return res;
  }

  render() {
    return html`
      <div class="panel">
        <div class="panel-header">
          <span class="logo"></span>
          <h1>PN3 Reports</h1>
        </div>

        <div class="date-container">
          <div class="date-field">
            <label class="date-label" for="startDate">from</label>
            <input
              id="startDate"
              type="date"
              class="date-input"
              .value=${this.startDate}
              @input=${(e) => (this.startDate = e.target.value)}
            />
          </div>
          <div class="date-field">
            <label class="date-label" for="endDate">to</label>
            <input
              id="endDate"
              type="date"
              class="date-input"
              .value=${this.endDate}
              @input=${(e) => (this.endDate = e.target.value)}
            />
          </div>
        </div>

        <div class="button-container">
          ${this.renderReportButtons()}
        </div>
      </div>
    `;
  }

  renderButton(reportType) {
    const rpt = reports[reportType];
    if (!rpt || rpt.show === false) {
      return nothing;
    }

    return html`
      <button
        class="report-button"
        @click=${() => this.selectReport(reportType)}
        ?disabled=${this.loadingReports[reportType] || this.isLoading}
      >
        ${this.loadingReports[reportType] ? 'Loading...' : rpt.name}
      </button>
    `;
  }

  renderReportButtons() {
    return html`
      <div class="button-group expense" label="EAF Reports">
        ${Object.keys(expense_reports).map(reportType => this.renderButton(reportType))}
      </div>
      <div class="button-group contribution" label="CAF Reports">
        ${Object.keys(contribution_reports).map(reportType => this.renderButton(reportType))}
      </div>
    `;
  }

  static get properties() {
    return {
      loginState: { type: String },
      currentPage: { type: String },
      startDate: { type: String },
      endDate: { type: String },
      userInfo: { type: Object },
      isLoading: { type: Boolean },
      loadingReports: { type: Object },
    };
  }
}

window.customElements.define("report-client", ReportClient);